import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  PackagePlus,
  Boxes,
  Truck,
  BarChart3,
  ShoppingCart,
  Store,
  Users,
  LogOut,
  Crown,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "operator", "shipping", "viewer"] },
  { to: "/production", label: "Production", icon: PackagePlus, roles: ["admin", "operator"] },
  { to: "/inventory", label: "Inventory", icon: Boxes, roles: ["admin", "operator", "shipping", "viewer"] },
  { to: "/shipments", label: "Shipments", icon: Truck, roles: ["admin", "shipping", "viewer"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "operator", "shipping", "viewer"] },
  { to: "/catalog/products", label: "Products", icon: ShoppingCart, roles: ["admin", "viewer"] },
  { to: "/catalog/customers", label: "Customers", icon: Store, roles: ["admin", "viewer"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "viewer";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      <aside className="mj-sidebar w-64 flex-shrink-0 hidden md:flex flex-col" data-testid="app-sidebar">
        <div className="px-5 py-6 border-b border-zinc-800 flex items-center gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center"
            style={{ background: "var(--mj-red)" }}
          >
            <Crown size={20} color="#E5B13A" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-xl font-bold leading-none text-white">
              Majesty<span style={{ color: "var(--mj-gold)" }}>IO</span>
            </div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-400 mt-1">
              OPERATIONS
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {NAV.filter((n) => n.roles.includes(role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) => (isActive ? "active" : "")}
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              <n.icon size={18} />
              <span className="text-sm">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-4">
          <div className="text-xs text-zinc-400 mb-2 tracking-widest uppercase">
            Signed in
          </div>
          <div className="text-sm font-medium text-white truncate">
            {user?.name || user?.email}
          </div>
          <div className="text-[11px] text-zinc-400 tracking-widest uppercase mt-1">
            {role}
          </div>
          <button
            onClick={handleLogout}
            className="mj-btn mj-btn-secondary w-full mt-4"
            data-testid="logout-btn"
            style={{ background: "transparent", color: "#fff", borderColor: "#3a3a36" }}
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="md:hidden bg-black text-white px-4 py-3 flex items-center justify-between">
          <span className="font-display text-lg">Majesty<span style={{ color: "var(--mj-gold)" }}>IO</span></span>
          <button
            onClick={handleLogout}
            className="text-xs uppercase tracking-widest"
            data-testid="logout-btn-mobile"
          >
            Sign out
          </button>
        </div>
        {children}
      </main>
    </div>
  );
}
